"use client";
import { useState, useRef, useEffect, type ReactNode } from "react";
import { HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";

// ? 도움말 아이콘 — 클릭 시 친절한 설명 팝오버. 처음 사용자용.
// 닫기: 바깥 클릭 / Escape (onBlur 미사용 → 팝오버 내 링크가 키보드로도 도달 가능, a11y).
export function HelpTip({
  children,
  title,
  className,
  side = "right",
}: {
  children: ReactNode;
  title?: string;
  className?: string;
  side?: "right" | "left";
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span ref={ref} className={cn("relative inline-flex align-middle", className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={title ? `도움말: ${title}` : "도움말"}
        className="text-neutral-300 transition-colors hover:text-neutral-600"
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
      {open && (
        <span
          role="tooltip"
          className={cn(
            "absolute top-5 z-50 w-64 rounded-md border border-neutral-200 bg-white p-3 text-left text-xs font-normal leading-relaxed text-neutral-600 shadow-lg",
            side === "right" ? "left-0" : "right-0",
          )}
        >
          {title && <span className="mb-1 block text-[13px] font-semibold text-neutral-800">{title}</span>}
          {children}
        </span>
      )}
    </span>
  );
}
